export const BOTH_FIELDS_REQUIRED_MESSAGE =
  'Enter both a policy number and a date of birth to search.';
export const INVALID_POLICY_NUMBER_MESSAGE = 'Enter a 10-digit or 13-digit policy number.';
export const INVALID_DATE_OF_BIRTH_MESSAGE = 'Enter a valid date of birth.';

/** Permanently visible above the field (PP-001 AC-3). */
export const POLICY_NUMBER_HINT =
  'Enter the 10-digit policy number or the 13-digit number, which includes the 3-digit company code followed by the 10-digit policy number.';

/** The same guidance on demand, plus where to read the number (PP-001 AC-5). */
export const POLICY_NUMBER_TOOLTIP =
  'Enter the 10-digit policy number or the 13-digit number, which includes the 3-digit company code followed by the 10-digit policy number, as shown on the member’s ID card.';

/** The 13-digit form is the longest accepted, so nothing beyond it can be typed (AC-10). */
export const POLICY_NUMBER_MAX_LENGTH = 13;

/**
 * Ten digits, or thirteen when the 3-digit company code is included (PP-001). `[0-9]` rather than `\d`
 * so it matches the server-side rule exactly.
 */
const POLICY_NUMBER_PATTERN = /^(?:[0-9]{10}|[0-9]{13})$/;

export interface PolicySearchErrors {
  readonly policyNumber: string | null;
  readonly dateOfBirth: string | null;
}

/** Drops anything that is not a digit, so a letter or a symbol never reaches the field (AC-11). */
export function digitsOnly(value: string): string {
  return value.replace(/[^0-9]/g, '');
}

export function validatePolicyNumber(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  return POLICY_NUMBER_PATTERN.test(trimmed) ? null : INVALID_POLICY_NUMBER_MESSAGE;
}

/** Matches PolicySearchRequestValidator: strictly after 1900-01-01. */
const EARLIEST_DATE_OF_BIRTH = Date.UTC(1900, 0, 1);

/**
 * A date of birth has to be a real date in the past. `<input type="date">` already refuses malformed
 * text, so what is left to catch is the future date and the implausible year.
 *
 * The bounds are the server's, exactly: PolicySearchRequestValidator wants strictly after 1900-01-01
 * and strictly before today in UTC. A looser rule here is not a kindness — it lets 1900-01-01 and
 * today's date through the field and brings them back as a 400 on submit, which the field could have
 * said the moment they were typed.
 */
export function validateDateOfBirth(value: string, today = new Date()): string | null {
  if (value.trim().length === 0) {
    return null;
  }

  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return INVALID_DATE_OF_BIRTH_MESSAGE;
  }

  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return parsed.getTime() <= EARLIEST_DATE_OF_BIRTH || parsed.getTime() >= todayUtc
    ? INVALID_DATE_OF_BIRTH_MESSAGE
    : null;
}

/** True only when both fields are present and neither is malformed (BR-02). */
export function canSubmit(policyNumber: string, dateOfBirth: string): boolean {
  return (
    policyNumber.trim().length > 0 &&
    dateOfBirth.trim().length > 0 &&
    validatePolicyNumber(policyNumber) === null &&
    validateDateOfBirth(dateOfBirth) === null
  );
}
